"use client";

/**
 * The card after a registration or an email confirmation: a cheering
 * scene, a greeting by name, one line, three paths to take next, and one
 * button that goes back to what the person was doing (the page that asked
 * them to sign up: the tax zones, an alert, an analysis), not a generic
 * "let's go" (owner, 2026-10-11: "it should depend on which flow you came
 * from", then "give a clue on what a person can do, at least"). It never
 * claims a Pro trial: signing up does not start one; an eligible account
 * gets a quiet link.
 *
 * The paths (lib/welcome-paths: three, in the order of the person's goal)
 * are plain rows, each with a scene from the house sets, the one the hub
 * already shows for the same step where it has one. They replaced icons
 * in tinted tiles under a capitals label (owner, 2026-10-31: the icons
 * "look kind of AI-ish"). The button is the card's one indigo; the rows
 * and the trial link stay grey.
 *
 * Two variants: "verified" (the account is live) and "needsVerification"
 * (confirm the address first).
 */
import { useEffect } from "react";
import { ChevronRight } from "lucide-react";
import { CHEVRON, LINK } from "@/lib/ui-classes";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import type { TranslationKey } from "@/lib/i18n";
import { trackEvent } from "@/lib/tracking";
import { readVisitorGoal } from "@/lib/visitor-goal";
import { destinationKey, pathsFor, PATH_HREF, type PathKind } from "@/lib/welcome-paths";
import Overlay from "@/components/Overlay";
import Illo from "@/components/Illo";
import Link from "@/lib/localized-link";

interface Props {
  open: boolean;
  /** Closes the card; the caller then goes to the destination. */
  onClose: () => void;
  /** A path was picked: the caller closes the card and goes to that
   *  path. It gets the app path without the locale ("/alerts"), as Link
   *  and lib/localized-router take it; the rendered href already carries
   *  the locale, and handing that over made "/az/az/alerts", a 404.
   *  Without it the rows are plain links. */
  onGo?: (href: string) => void;
  /** Right after an email/password registration, before the address is
   *  confirmed: the copy says what the confirmation unlocks. */
  needsVerification?: boolean;
  /** Where the person is taken on close (the page that sent them to
   *  sign up), so the button can say it. Null or "/" means the home. */
  destination?: string | null;
}

/** Each path's scene: the art the hub already shows for the same step
 *  (the roadmap's analyze, alert, map, calculate and sale-tax steps),
 *  else a scene from the spots set (the search, the chat). `zoom` crops a
 *  scene drawn with wide margins. */
const ART: Record<PathKind, { base: string; zoom?: string }> = {
  analyze: { base: "/illustrations/finder/entry" },
  alerts: { base: "/illustrations/spots/empty_alerts" },
  map: { base: "/illustrations/finder/opts/lifestyle_center", zoom: "scale-[1.3]" },
  calculate: { base: "/illustrations/finder/budget" },
  zones: { base: "/illustrations/finder/opts/deed_doc" },
  listings: { base: "/illustrations/spots/searching" },
  chat: { base: "/illustrations/spots/empty_chat" },
};

export default function WelcomeModal({ open, onClose, onGo, needsVerification = false, destination = null }: Props) {
  const t = useT();
  const { user } = useAuth();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const name = (user?.name || "").trim().split(/\s+/)[0] || "";
  const goKey = destinationKey(destination);
  const away = goKey !== "welcome.go.home";
  const trialOffer = !needsVerification && user?.pro_trial_eligible === true;
  // The account's goal, else the visitor pick it is about to inherit
  // (lib/use-personal copies it over once the hub loads), so the order
  // is right from the first frame and never reshuffles.
  const goal = user?.goal || readVisitorGoal();
  const paths = pathsFor(goKey, goal);

  return (
    <Overlay
      open={open}
      onClose={onClose}
      className="flex items-end justify-center p-0 sm:items-center sm:p-4"
      backdropClassName="bg-gray-900/40 backdrop-blur-sm"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-t-3xl border border-gray-200 bg-white shadow-2xl sm:max-h-[calc(100dvh-3rem)] sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The scene, wide, on the paper tone. */}
        <div className="relative h-36 w-full shrink-0 overflow-hidden bg-[rgb(250,249,246)] dark:bg-[rgb(30,36,52)] sm:h-44">
          <Illo base="/illustrations/welcome/welcome" className="absolute inset-0 h-full w-full object-contain object-center p-1.5" />
        </div>

        <div className="min-h-0 overflow-y-auto px-5 pt-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:px-6 sm:pt-6 sm:pb-6">
          <h2 id="welcome-title" className="text-2xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-[1.7rem]">
            {needsVerification ? t("welcome.verify.title") : `${t("welcome.title")}${name ? `, ${name}` : ""}!`}
          </h2>
          <p className="mt-1.5 text-[15px] leading-relaxed text-gray-700">
            {needsVerification ? t("welcome.verify.subtitle") : t("welcome.subtitle")}
            {!needsVerification && away && <> {t("welcome.back")}</>}
          </p>

          {/* Three paths, the goal's own first. The subtitle asks where to
              start, so no label repeats it. A hairline between rows starts
              at the words, not under the scenes. The rows arrive one after
              another; only the visible twin of each scene is fetched. */}
          <ul className="-mx-2 mt-3">
            {paths.map((kind, i) => {
              const art = ART[kind];
              const href = PATH_HREF[kind];
              return (
                <li
                  key={kind}
                  style={{ animationDelay: `${110 + i * 70}ms` }}
                  className="animate-[welcome-row-in_0.22s_ease-out_both] motion-reduce:animate-none"
                >
                  <Link
                    href={href}
                    onClick={(e) => {
                      trackEvent("welcome.path", { path: kind, pos: i + 1, goal: goal || "none", from: goKey.replace("welcome.go.", "") });
                      if (!onGo) return;
                      e.preventDefault();
                      onGo(href);
                    }}
                    className="no-scale group flex items-center gap-4 rounded-xl px-2 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    <span className="relative my-2.5 block h-16 w-16 shrink-0 overflow-hidden rounded-xl">
                      <Illo base={art.base} loading="lazy" className={`h-full w-full object-cover ${art.zoom ?? ""}`} />
                    </span>
                    <span className={`flex min-w-0 flex-1 items-center gap-3 self-stretch py-3 ${i > 0 ? "border-t border-gray-200" : ""}`}>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[15px] font-semibold leading-snug text-gray-900">{t(`welcome.try.${kind}.t` as TranslationKey)}</span>
                        <span className="mt-0.5 block text-[13px] leading-snug text-gray-600">{t(`welcome.try.${kind}.d` as TranslationKey)}</span>
                      </span>
                      <ChevronRight className={`h-4 w-4 shrink-0 ${CHEVRON} transition-colors group-hover:text-gray-600`} aria-hidden />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            onClick={onClose}
            className="mt-4 inline-flex h-12 w-full items-center justify-center rounded-xl bg-indigo-600 px-5 text-[15px] font-semibold text-white transition-colors hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            {needsVerification ? t("welcome.verify.cta") : t(goKey)}
          </button>
          {trialOffer && (
            <p className="mt-3 text-center text-sm">
              {/* A grey link, not a second indigo. */}
              <Link href="/pricing" className={LINK}>{t("welcome.trial")}</Link>
            </p>
          )}
        </div>
      </div>
    </Overlay>
  );
}
